import { useMemo } from "react"
import { Panel } from "../Panel"
import {
  coverState,
  generateWarehouseData,
  type CoverState,
  type DcRow,
  type InboundPo,
  type SkuRow,
  type SlaStatus,
} from "../warehouse-data"

/* ----- palette ----------------------------------------------------------- */

// Severity colors (terminal heatmap). Healthy → low → critical.
const COVER_FILL: Record<CoverState, string> = {
  healthy: "#22c55e",
  low: "#facc15",
  critical: "#ef4444",
}

// Heatmap cells use opacity scaled by how deep into the band the value sits,
// so the grid reads like a true intensity map rather than 3 flat colors.
function cellStyle(days: number): { background: string } {
  const state = coverState(days)
  const base = COVER_FILL[state]
  let alpha: number
  if (state === "critical") {
    // 0d (worst) → ~0.95, just-under-3d → ~0.55
    alpha = 0.55 + (1 - days / 3) * 0.4
  } else if (state === "low") {
    // 3d → 0.5, 10d → 0.18
    alpha = 0.5 - ((days - 3) / 7) * 0.32
  } else {
    // ≥10d, clamp so very-deep cover doesn't blow out
    alpha = 0.16 + Math.min(1, (days - 10) / 25) * 0.22
  }
  return { background: hexA(base, Math.max(0.12, Math.min(0.97, alpha))) }
}

function hexA(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`
}

const SLA_PILL: Record<SlaStatus, { label: string; cls: string }> = {
  ok: { label: "OK", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  "at-risk": { label: "AT-RISK", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  breached: { label: "BREACHED", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
}

const INBOUND_TONE: Record<InboundPo["status"], string> = {
  "on-time": "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  late: "text-red-400 border-red-500/30 bg-red-500/10",
  early: "text-sky-400 border-sky-500/30 bg-sky-500/10",
}

/* ----- tiny building blocks --------------------------------------------- */

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[9px] uppercase tracking-[0.14em] text-[color:var(--text-dim)]">
      {children}
    </span>
  )
}

function MicroBars({ values, color }: { values: number[]; color: string }) {
  const max = Math.max(...values, 1)
  const min = Math.min(...values)
  const span = Math.max(1, max - min)
  return (
    <div className="flex items-end gap-[1px] h-3.5">
      {values.map((v, i) => (
        <span
          key={i}
          className="w-[3px] rounded-[1px]"
          style={{
            height: `${20 + ((v - min) / span) * 80}%`,
            background: color,
            opacity: 0.4 + (i / Math.max(1, values.length - 1)) * 0.6,
          }}
        />
      ))}
    </div>
  )
}

/* ----- sections ---------------------------------------------------------- */

function Heatmap({ skus, days }: { skus: SkuRow[]; days: string[] }) {
  // Show a sampled subset of day columns as header ticks to avoid clutter but
  // keep the strip dense. Every cell still renders.
  return (
    <div className="px-3 pt-2 pb-3">
      <div className="flex items-center justify-between mb-1.5">
        <Label>Stock-Health · days-of-cover</Label>
        <div className="flex items-center gap-2.5 text-[8px] uppercase tracking-wider text-[color:var(--text-dim)]">
          <LegendDot color={COVER_FILL.healthy} text="≥10d" />
          <LegendDot color={COVER_FILL.low} text="3–9d" />
          <LegendDot color={COVER_FILL.critical} text="<3d" />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse" style={{ minWidth: 520 }}>
          <thead>
            <tr>
              <th className="text-left sticky left-0 z-10 bg-[color:var(--surface)] pr-2 pb-1">
                <span className="text-[8px] uppercase tracking-wider text-[color:var(--text-dim)]">
                  SKU
                </span>
              </th>
              {days.map((d, i) => (
                <th key={i} className="pb-1 px-0">
                  <span className="text-[7px] tabular-nums text-[color:var(--text-dim)] leading-none block text-center">
                    {i % 2 === 0 ? d.replace(/^\w+ /, "") : ""}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {skus.map((s) => {
              const st = coverState(s.coverNow)
              return (
                <tr key={s.sku} className="group">
                  <td className="sticky left-0 z-10 bg-[color:var(--surface)] group-hover:bg-[color:var(--surface-hover)] pr-2 py-[2px] align-middle">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ background: COVER_FILL[st] }}
                      />
                      <div className="min-w-0 leading-tight">
                        <div className="text-[10px] font-semibold text-[color:var(--text)] tabular-nums">
                          {s.sku}
                        </div>
                        <div className="text-[8px] text-[color:var(--text-dim)] truncate max-w-[110px]">
                          {s.name}
                        </div>
                      </div>
                      <div className="ml-auto text-right shrink-0 pl-1">
                        <div
                          className="text-[10px] font-semibold tabular-nums leading-none"
                          style={{ color: COVER_FILL[st] }}
                        >
                          {s.coverNow.toFixed(1)}d
                        </div>
                        <div className="text-[8px] text-[color:var(--text-dim)] tabular-nums leading-none mt-[1px]">
                          {s.onHand.toLocaleString()}u
                        </div>
                      </div>
                    </div>
                  </td>
                  {s.cover.map((c, i) => (
                    <td
                      key={i}
                      className="h-[18px] p-[1px] align-middle"
                      title={`${s.sku} · ${days[i] ?? ""} · ${c.toFixed(1)}d cover`}
                    >
                      <div
                        className="w-full h-full rounded-[2px] border border-black/20"
                        style={cellStyle(c)}
                      />
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function LegendDot({ color, text }: { color: string; text: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="w-2 h-2 rounded-[2px]" style={{ background: color }} />
      <span className="tabular-nums">{text}</span>
    </span>
  )
}

function SlaBoard({ dcs }: { dcs: DcRow[] }) {
  return (
    <div className="px-3 py-2 border-t border-[color:var(--border)]">
      <Label>DC SLA Board · on-time fulfilment</Label>
      <div className="mt-1.5 grid grid-cols-2 gap-1.5">
        {dcs.map((dc) => {
          const pill = SLA_PILL[dc.status]
          const barColor =
            dc.status === "ok" ? COVER_FILL.healthy : dc.status === "at-risk" ? COVER_FILL.low : COVER_FILL.critical
          return (
            <div
              key={dc.code}
              className="border border-[color:var(--border)] rounded p-2 bg-[color:var(--surface-hover)]/40"
            >
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-semibold tabular-nums text-[color:var(--text)]">
                  {dc.code}
                </span>
                <span className="text-[8px] uppercase tracking-wider text-[color:var(--text-dim)] truncate">
                  {dc.region}
                </span>
                <span
                  className={`ml-auto text-[8px] px-1 py-[1px] rounded border uppercase tracking-wider ${pill.cls}`}
                >
                  {pill.label}
                </span>
              </div>

              <div className="mt-1.5 flex items-end justify-between gap-2">
                <div className="leading-none">
                  <span className="text-[15px] font-bold tabular-nums" style={{ color: barColor }}>
                    {dc.onTimePct.toFixed(1)}
                  </span>
                  <span className="text-[9px] text-[color:var(--text-dim)]">%</span>
                </div>
                <MicroBars values={dc.trend7} color={barColor} />
              </div>

              <div className="mt-1.5 h-1 w-full rounded-full bg-[color:var(--border)] overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${dc.onTimePct}%`, background: barColor }}
                />
              </div>

              <div className="mt-1 flex items-center gap-2.5 text-[8px] uppercase tracking-wider text-[color:var(--text-dim)]">
                <span>
                  open<span className="text-[color:var(--text-muted)] ml-1 tabular-nums normal-case">{dc.openLines}</span>
                </span>
                <span>
                  backlog
                  <span
                    className={`ml-1 tabular-nums normal-case ${dc.backlog > 50 ? "text-red-400" : "text-[color:var(--text-muted)]"}`}
                  >
                    {dc.backlog}
                  </span>
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function InboundStrip({ inbound }: { inbound: InboundPo[] }) {
  return (
    <div className="px-3 py-2 border-t border-[color:var(--border)]">
      <Label>Inbound Timeline · upcoming POs</Label>
      <div className="mt-1.5 flex gap-1.5 overflow-x-auto pb-1">
        {inbound.map((po) => {
          const tone = INBOUND_TONE[po.status]
          return (
            <div
              key={po.po}
              className="shrink-0 w-[118px] border border-[color:var(--border)] rounded p-1.5 bg-[color:var(--surface-hover)]/40"
            >
              <div className="flex items-center justify-between gap-1">
                <span className="text-[9px] font-semibold tabular-nums text-[color:var(--text)]">
                  {po.po}
                </span>
                <span className={`text-[7px] px-1 rounded border uppercase tracking-wider ${tone}`}>
                  {po.status === "on-time" ? "ON-TIME" : po.status === "late" ? `+${po.slipDays}d` : `${po.slipDays}d`}
                </span>
              </div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-[8px] uppercase tracking-wider text-[color:var(--text-dim)]">eta</span>
                <span className="text-[10px] font-semibold tabular-nums text-[color:var(--text)]">
                  {po.etaInDays === 0 ? "today" : `+${po.etaInDays}d`}
                </span>
              </div>
              <div className="mt-0.5 flex items-baseline justify-between">
                <span className="text-[8px] text-[color:var(--text-dim)] tabular-nums">{po.sku}</span>
                <span className="text-[9px] tabular-nums text-[color:var(--text-muted)]">
                  {po.qty.toLocaleString()}u
                </span>
              </div>
              <div className="text-[7px] text-[color:var(--text-dim)] truncate mt-0.5">{po.supplier}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ----- panel ------------------------------------------------------------- */

export function WarehouseOps() {
  // Seeded + memoized: stable across re-renders, no Math.random at render time.
  const data = useMemo(() => generateWarehouseData(42, 14), [])
  const { summary } = data

  return (
    <Panel
      title="Warehouse Ops"
      demo
      count={`${summary.skuCount} SKU`}
      helpText="Synthetic stock-health, DC SLA, and inbound view. Cover = on-hand ÷ daily demand. Heatmap colors days-of-cover: green ≥10d, amber 3–9d, red <3d."
      footer={
        <div className="flex items-center gap-3 flex-wrap">
          <span>
            <span className="text-red-400 tabular-nums">{summary.criticalCount}</span> critical
          </span>
          <span>
            <span className="text-amber-400 tabular-nums">{summary.lowCount}</span> low
          </span>
          <span className="text-[color:var(--text-dim)]">·</span>
          <span>
            avg on-time <span className="text-[color:var(--text-muted)] tabular-nums">{summary.avgOnTime}%</span>
          </span>
          <span>
            breached DC <span className="text-red-400 tabular-nums">{summary.breachedDcs}</span>
          </span>
          <span className="text-[color:var(--text-dim)]">·</span>
          <span>
            inbound <span className="text-[color:var(--text-muted)] tabular-nums">{summary.inboundUnits.toLocaleString()}u</span>
          </span>
        </div>
      }
    >
      <Heatmap skus={data.skus} days={data.days} />
      <SlaBoard dcs={data.dcs} />
      <InboundStrip inbound={data.inbound} />
    </Panel>
  )
}
